import React, { useState, useEffect, memo } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { getCourseSurvey, submitCourseSurvey } from '../../../../services/userService';

const PRIMARY = '#7083FF';

const CourseSurvey = memo(function CourseSurvey({ surveyId, moduleData, onSurveyComplete, currentLessonIndex, totalLessons, onNextLesson }) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState(() => {
    try {
      const saved = localStorage.getItem(`survey-answers-${surveyId}`);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [showResults, setShowResults] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assessmentTitle, setAssessmentTitle] = useState('');

  useEffect(() => {
    setCurrentQuestion(0);
    setShowResults(false);
    setQuizStarted(false);
    setQuestions([]);
    setTotalQuestions(0);
    setIsLoading(true);
    setError(null);
    setIsSubmitting(false);
    setAssessmentTitle('');
    try {
      const saved = localStorage.getItem(`survey-answers-${surveyId}`);
      setSelectedAnswers(saved ? JSON.parse(saved) : {});
    } catch {
      setSelectedAnswers({});
    }
  }, [surveyId]);

  useEffect(() => {
    if (moduleData?.completed === 1) {
      setShowResults(true);
      setIsLoading(false);
    } else {
      fetchSurveyQuestions();
    }
  }, [surveyId, moduleData?.completed]);

  useEffect(() => {
    if (Object.keys(selectedAnswers).length > 0) {
      localStorage.setItem(`survey-answers-${surveyId}`, JSON.stringify(selectedAnswers));
    }
  }, [selectedAnswers, surveyId]);

  async function fetchSurveyQuestions() {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getCourseSurvey({ assessmentId: surveyId });
      if (response?.data) {
        setQuestions(response.data.questions || []);
        setTotalQuestions(response.data.total_questions || 0);
        setAssessmentTitle(response.data.assessment_title || '');
      } else {
        setError('Failed to load survey questions. Please try again.');
      }
    } catch (err) {
      setError('An error occurred while loading the survey. Please try again later.');
      setQuestions([]);
      setTotalQuestions(0);
    } finally {
      setIsLoading(false);
    }
  }

  const handleStartSurvey = () => setQuizStarted(true);

  const handleOptionSelect = (questionId, optionId, optionValue) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [questionId]: {
        assessment_id: surveyId,
        question_id: questionId,
        selected_option_id: optionId,
        selected_value: optionValue || '',
      },
    });
  };

  const isQuestionAnswered = (questionId) => !!selectedAnswers[questionId];

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const validateSurvey = () => {
    const answered = questions.filter((q) => isQuestionAnswered(q.id));
    if (answered.length === 0) {
      return 'Please answer at least one question before submitting the survey.';
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationMessage = validateSurvey();
    if (validationMessage) {
      toast.warning(validationMessage, { position: 'top-center', autoClose: 4000 });
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      const formattedAnswers = Object.values(selectedAnswers);
      await submitCourseSurvey(formattedAnswers);
      setShowResults(true);
      localStorage.removeItem(`survey-answers-${surveyId}`);
      if (onSurveyComplete) {
        onSurveyComplete(surveyId);
      }
      toast.success('Survey submitted successfully.', { position: 'top-center', autoClose: 3000 });
    } catch (err) {
      setError('Failed to submit survey. Please try again.');
      toast.error('Failed to submit survey. Please try again.', { position: 'top-center' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Completed state
  if (moduleData?.completed === 1 || showResults) {
    return (
      <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-8 sm:p-10">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-50 text-green-600 mb-6">
            <Icon icon="mdi:check-circle" width={48} height={48} />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">You have completed the survey</h2>
          <p className="text-gray-500">Thank you for your feedback.</p>
        </div>
        <div className="flex items-center justify-between gap-4 pt-4 border-t border-gray-200">
          <span className="text-sm text-gray-500">Lesson <span className="font-semibold text-gray-900">{currentLessonIndex}</span> of {totalLessons}</span>
          {typeof onNextLesson === 'function' && totalLessons > 0 && (
            <button
              type="button"
              onClick={onNextLesson}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-[#7083FF] hover:bg-[#5f73e6] transition-colors"
            >
              {currentLessonIndex < totalLessons ? 'Next Lesson' : 'Explore Courses'}
              <Icon icon="mdi:arrow-right" width="18" height="18" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Loading
  if (isLoading) {
    return (
      <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-12 flex flex-col items-center justify-center min-h-[280px]">
        <div
          className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-[#7083FF]"
          role="status"
          aria-label="Loading"
        />
        <p className="mt-4 text-gray-500 text-sm">Loading survey questions...</p>
      </div>
    );
  }

  // Error
  if (error && !questions.length) {
    return (
      <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-50 text-red-500 mb-4">
          <Icon icon="mdi:alert-circle" width={40} height={40} />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Something went wrong</h3>
        <p className="text-gray-600 text-sm mb-6">{error}</p>
        <button
          type="button"
          onClick={fetchSurveyQuestions}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-medium text-sm shadow-sm hover:opacity-90 transition-opacity"
          style={{ backgroundColor: PRIMARY }}
        >
          <Icon icon="mdi:refresh" width={18} height={18} />
          Try again
        </button>
      </div>
    );
  }

  // No questions
  if (!questions.length) {
    return (
      <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 text-gray-400 mb-4">
          <Icon icon="mdi:clipboard-text-outline" width={40} height={40} />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">No questions yet</h3>
        <p className="text-gray-500 text-sm">This survey doesn’t have any questions at the moment.</p>
      </div>
    );
  }

  // Start screen
  if (!quizStarted) {
    return (
      <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-6 sm:p-8">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl mb-4" style={{ backgroundColor: `${PRIMARY}15` }}>
            <Icon icon="mdi:clipboard-text-outline" width={32} height={32} style={{ color: PRIMARY }} />
          </div>
          {assessmentTitle && (
            <h1 className="text-lg font-semibold text-gray-900 mb-1 break-words">{assessmentTitle}</h1>
          )}
          <p className="text-gray-500 text-sm">Survey</p>
        </div>
        <ul className="space-y-3 mb-8">
          {[
            { icon: 'mdi:play-circle-outline', text: 'Click Start to begin the survey.' },
            { icon: 'mdi:arrow-left-circle-outline', text: 'You can go back to previous questions anytime.' },
            { icon: 'mdi:content-save-outline', text: 'Answers are saved automatically.' },
            { icon: 'mdi:help-circle-outline', text: 'Answer at least one question to submit.' },
          ].map((item, i) => (
            <li key={i} className="flex items-center gap-3 text-gray-600 text-sm">
              <Icon icon={item.icon} width={20} height={20} className="text-gray-400 shrink-0" />
              <span>{item.text}</span>
            </li>
          ))}
        </ul>
        <div className="text-center">
          <button
            type="button"
            onClick={handleStartSurvey}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-medium text-sm shadow-sm hover:opacity-90 transition-opacity"
            style={{ backgroundColor: PRIMARY }}
          >
            Start survey
            <Icon icon="mdi:arrow-right" width={20} height={20} />
          </button>
        </div>
      </div>
    );
  }

  // Question view
  const question = questions[currentQuestion];
  const isLastQuestion = currentQuestion === questions.length - 1;
  const progress = questions.length ? ((currentQuestion + 1) / questions.length) * 100 : 0;

  return (
    <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-6 sm:p-8">
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm text-gray-500">
          Question {currentQuestion + 1} of {questions.length}
        </span>
        <span className="text-sm font-medium" style={{ color: PRIMARY }}>
          {Math.round(progress)}% complete
        </span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-6">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${progress}%`, backgroundColor: PRIMARY }}
          role="progressbar"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>

      <div className="mb-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4 leading-snug">
          {currentQuestion + 1}. {question.question}
        </h3>
        <div className="space-y-3">
          {question.options.map((option) => {
            const isSelected = selectedAnswers[question.id]?.selected_option_id === option.id;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => handleOptionSelect(question.id, option.id, option.value)}
                className={`w-full text-left px-4 py-3.5 rounded-xl border-2 transition-all duration-200 flex items-center gap-3 ${
                  isSelected
                    ? 'border-[#7083FF] bg-[#7083FF]/8 text-gray-900'
                    : 'border-gray-200 bg-gray-50/50 hover:border-gray-300 hover:bg-gray-50 text-gray-800'
                }`}
              >
                <span
                  className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    isSelected ? 'border-[#7083FF] bg-[#7083FF]' : 'border-gray-300'
                  }`}
                >
                  {isSelected && <Icon icon="mdi:check" width={14} height={14} className="text-white" />}
                </span>
                <span className="text-sm font-medium">{option.value}</span>
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <div>
          {currentQuestion > 0 && (
            <button
              type="button"
              onClick={handlePrevious}
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 font-medium text-sm disabled:opacity-50 transition-colors"
              style={{ borderColor: PRIMARY, color: PRIMARY }}
            >
              <Icon icon="mdi:chevron-left" width={20} height={20} />
              Previous
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isLastQuestion ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-medium text-sm shadow-sm hover:opacity-90 disabled:opacity-50 transition-opacity"
              style={{ backgroundColor: PRIMARY }}
            >
              Next
              <Icon icon="mdi:chevron-right" width={20} height={20} />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-medium text-sm shadow-sm hover:opacity-90 disabled:opacity-50 transition-opacity bg-emerald-600 hover:bg-emerald-700"
            >
              {isSubmitting ? (
                <>
                  <span
                    className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"
                    role="status"
                    aria-hidden
                  />
                  Submitting...
                </>
              ) : (
                <>
                  Submit
                  <Icon icon="mdi:check" width={20} height={20} />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

export default CourseSurvey;
